import type { LbEventPhase, LbEventSubType, LbEventType } from './event-types';
import type { BasePollingService } from './options';
import type { VarIncident, VarReason, VarResult, VarStatus } from './var-types';

export const PartType = {
  COMPOSER: 'Composer',
  UEFA_EDITORIAL: 'UefaEditorial',
  POLL: 'Poll',
  FREE_PHOTO: 'FreePhoto',
  SOCIAL: 'Social',
  MATCH_SUMMARY: 'MatchSummary',
  MATCH_EVENTS: 'MatchEvents',
  CARD: 'Card',
  VIDEO_POST: 'VideoPost',
} as const;
export type PartType = (typeof PartType)[keyof typeof PartType];

export interface LbImage {
  figcredits: string;
  figcaption: string;
  wide?: { w1: string; w2: string; w3: string; w4: string };
  square?: { s1: string; s2: string; s3: string; s4: string };
  ultrawide?: { ultrawide: string };
  portrait?: { p1: string; p2: string; p3: string };
  free?: { f1: string; f2: string; f3: string; f4: string };
}

export type LbEventVarInfo = {
  incident?: VarIncident;
  reason?: VarReason;
  result?: VarResult;
  status?: VarStatus;
};

/** @deprecated use `LbPostEvent` */
export type LbPostEventOld = {
  eventId: string;
  eventType: string;
  eventDateTimeUTC?: string;
  eventSubType?: string;
  eventExtraSubType?: string;
  eventPhase?: string;
  lbPostEventPhase?: string;
  eventMinute?: string;
  eventSecond?: string;
  eventDisplayMinute?: string;
  eventDisplayInjuryTimeMinute?: number;
  eventDisplayInjuryTimeExtraMinute?: string;
  eventTranslation?: string;
  eventTranslationFormatted?: string;
  eventStrTime?: string;
  idMatch?: string;
  idPlayer?: string;
  idPlayerTo?: string;
  namePlayer?: string;
  namePlayerTo?: string;
  fieldPositionPlayer?: string;
  fieldPositionPlayerTo?: string;
  imageUrlPlayer?: string;
  imageUrlPlayerTo?: string;
  idTeam?: string;
  idTeamTo?: string;
  teamCountryCode?: string;
  teamToCountryCode?: string;
};

export type LbPostEvent = Omit<LbPostEventOld, 'lbPostEventPhase'> & {
  eventType: LbEventType;
  eventSubType?: LbEventSubType;
  eventExtraSubType?: LbEventSubType;
  eventPhase?: LbEventPhase;
  varInfo?: LbEventVarInfo;
};

export type LbPostData = {
  isSticky?: boolean;
  forceNeutral?: boolean;
  lbPostType: string;
  lbPostPublisher?: string;
  postAmbassador?: string;
  lbPostTimestamp?: string;
  /** @deprecated use `lbPostFSPEvent` field */
  lbPostEvent?: LbPostEventOld;
  lbPostFSPEvent?: LbPostEvent;
};

export type LbPartData = {
  Title?: string;
  Id?: string;
  Description?: string;
  Text?: string;
  Image?: LbImage;
  ImageUrl?: string;
  Editorial?: any;
  HlsUrl?: string;
  HideFromWeb?: boolean;
  Section?: string;
  Kind?: string;
  Url?: string;
  MediaType?: string;
  Component?: string;
  Type?: string;
  ForeignId?: string;
  image?: LbImage;
  imgUrl?: string;
  imgWidth?: string;
  imgHeight?: string;
};

export type LbPart = {
  data?: LbPartData;
  datasource?: PartType;
  dataId?: string;
  resourceId?: string;
};

export type LbPostSponsor = {
  code: string;
};

export interface LbPost {
  id?: string;
  isNew?: boolean;
  parts?: LbPart[];
  date?: string;
  reactions?: { likes?: number; dislikes?: number };
  author?: { name: string };
  lastModifier?: { name: string };
  lastUpdateDate?: string;
  liveblogPostData?: LbPostData;
  sponsors?: LbPostSponsor[];
}

export const LbStatus = {
  OK: 'OK',
  LOADING: 'LOADING',
  LOADING_MORE: 'LOADING_MORE',
  EMPTY: 'EMPTY',
  FINISHED: 'FINISHED',
  ERROR: 'ERROR',
} as const;
export type LbStatus = (typeof LbStatus)[keyof typeof LbStatus];

export const LbUpdateType = {
  LOAD_POSTS: 'PostsLoaded',
  ADD_POST: 'add',
  UPDATE_POST: 'update',
  DELETE_POST: 'delete',
  SET_STICKY_POST: 'StickyPostSet',
  UNSET_STICKY_POST: 'StickyPostUnset',
} as const;
export type LbUpdateType = (typeof LbUpdateType)[keyof typeof LbUpdateType];

export interface LbUpdate {
  postId: string;
  body?: LbPost;
  action: LbUpdateType;
}

export const SocialType = {
  FACEBOOK: 'facebook',
  TWITTER: 'twitter',
  INSTAGRAM: 'instagram',
  YOUTUBE: 'youtube',
} as const;
export type SocialType = (typeof SocialType)[keyof typeof SocialType];

export type LbState = {
  blogId?: string;
  items: LbPost[];
  status: LbStatus;
  lastVersion?: string;
};

export type LbActions = {
  showAddedPosts: () => void;
  loadPosts: (service: BasePollingService) => Promise<void>;
  loadUpdates: (service: BasePollingService) => Promise<void>;
};
